// How a `tool` utterance renders in the conversation. Customers send whatever
// their harness emits — a JSON object, a JSON string, a `{name, arguments}` /
// `{tool, input}` call shape, key=value lines, or prose — so this degrades to
// readable monospace and never to a crash or a blank.

export interface ToolCallPresentation {
  // The rest of the payload (pretty-printed JSON or the raw text).
  body: string;
  // `name(args)` when the payload is a recognisable call shape.
  headline: string | null;
}

function parseJson(text: string): unknown {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return undefined;
  }
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function compact(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

export function presentToolCall(text: string): ToolCallPresentation {
  const parsed = parseJson(text);
  if (parsed === undefined) {
    return { body: text, headline: null };
  }
  // A JSON string wrapping the real payload (double-encoded) unwraps once.
  const payload =
    typeof parsed === "string" ? (parseJson(parsed) ?? parsed) : parsed;
  if (typeof payload === "string") {
    return { body: payload, headline: null };
  }
  if (isRecord(payload)) {
    const name =
      typeof payload.name === "string"
        ? payload.name
        : typeof payload.tool === "string"
          ? payload.tool
          : null;
    const argsKey =
      "arguments" in payload
        ? "arguments"
        : "input" in payload
          ? "input"
          : null;
    if (name != null && argsKey != null) {
      const rest = Object.fromEntries(
        Object.entries(payload).filter(
          ([key]) => key !== "name" && key !== "tool" && key !== argsKey,
        ),
      );
      return {
        body: Object.keys(rest).length > 0 ? JSON.stringify(rest, null, 2) : "",
        headline: `${name}(${compact(payload[argsKey])})`,
      };
    }
  }
  return { body: JSON.stringify(payload, null, 2), headline: null };
}
